import React, { useState, useRef, useCallback, useEffect } from 'react';
import { User, Mail, Lock, AlertCircle, CheckCircle, Loader } from 'lucide-react';
import { apiClient } from '../../utils/apiClient';
import { API_URL } from '../../config';
import ForgotPasswordModal from './ForgotPasswordModal';
import { EmailCodeModal } from './EmailCodeModal';

const GOOGLE_CLIENT_ID = '1063956641225-puhe1cas87adtt95v25aes3ocgk9tff1.apps.googleusercontent.com';

export default function AuthForm({ mode, onSuccess, onBack }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    acceptTerms: false
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [referralCode, setReferralCode] = useState(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const formRef = useRef(null);
  const googleBtnRef = useRef(null);

  // NEU: Step-Up Auth States
  const [showEmailCodeModal, setShowEmailCodeModal] = useState(false);
  const [pendingUserId, setPendingUserId] = useState(null);
  const [emailCodeError, setEmailCodeError] = useState('');
  const [isVerifyingCode, setIsVerifyingCode] = useState(false);

  // Double-Opt-In: Bildschirm nach erfolgreicher Registrierung.
  const [verificationSentEmail, setVerificationSentEmail] = useState(null);
  const [isResending, setIsResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  // Bei Login mit unbestätigter E-Mail zeigen wir dieselbe Info + Resend-Button.
  const [loginEmailUnverified, setLoginEmailUnverified] = useState(false);

  const isRegister = mode === 'register';

  // Google Identity Services: einmalig initialisieren + Button rendern (FedCM-frei)
  useEffect(() => {
    let cancelled = false;
    const ensureScript = () => new Promise((resolve, reject) => {
      if (window.google?.accounts?.id) return resolve();
      const existing = document.querySelector('script[src*="accounts.google.com/gsi/client"]');
      if (existing) {
        const check = setInterval(() => {
          if (window.google?.accounts?.id) { clearInterval(check); resolve(); }
        }, 100);
        setTimeout(() => { clearInterval(check); reject(new Error('GSI Timeout')); }, 8000);
        return;
      }
      const s = document.createElement('script');
      s.src = 'https://accounts.google.com/gsi/client';
      s.async = true;
      s.defer = true;
      s.onload = () => {
        const check = setInterval(() => {
          if (window.google?.accounts?.id) { clearInterval(check); resolve(); }
        }, 100);
        setTimeout(() => { clearInterval(check); reject(new Error('GSI Timeout')); }, 8000);
      };
      s.onerror = reject;
      document.head.appendChild(s);
    });

    const handleCredential = async (response) => {
      try {
        setIsLoading(true);
        const res = await fetch(`${API_URL}/api/auth/google/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id_token: response.credential })
        });
        const data = await res.json();
        if (res.ok && data.token) {
          localStorage.setItem('snapshot_token', data.token);
          localStorage.setItem('snapshot_user', JSON.stringify(data.user));
          onSuccess(data.user);
        } else {
          setErrors({ general: data.detail || 'Google Login fehlgeschlagen' });
        }
      } catch (err) {
        setErrors({ general: 'Google Login fehlgeschlagen' });
      } finally {
        setIsLoading(false);
      }
    };

    ensureScript()
      .then(() => {
        if (cancelled) return;
        try {
          window.google.accounts.id.initialize({
            client_id: GOOGLE_CLIENT_ID,
            callback: handleCredential,
            ux_mode: 'popup',
            use_fedcm_for_prompt: false
          });
          if (googleBtnRef.current) {
            googleBtnRef.current.innerHTML = '';
            window.google.accounts.id.renderButton(googleBtnRef.current, {
              type: 'standard',
              theme: 'filled_black',
              size: 'large',
              shape: 'rectangular',
              text: isRegister ? 'signup_with' : 'signin_with',
              logo_alignment: 'left',
              width: 400
            });
          }
        } catch (e) {
          console.error('Google init/render error:', e);
        }
      })
      .catch((e) => console.error('GSI script error:', e));

    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [isRegister]);
  
  // NEU: Lese ref Parameter aus URL ODER localStorage
  React.useEffect(() => {
    // 1. Prüfe URL Parameter
    const urlParams = new URLSearchParams(window.location.search);
    const refFromUrl = urlParams.get('ref');
    
    // 2. Prüfe localStorage (falls von /auth Route gespeichert)
    const refFromStorage = localStorage.getItem('snapshot_referral_code');
    
    const ref = refFromUrl || refFromStorage;
    
    if (ref) {
      setReferralCode(ref);
      console.log('🔗 Referral Code erkannt:', ref, '(Quelle:', refFromUrl ? 'URL' : 'localStorage', ')');
      
      // Speichere in localStorage für späteren Gebrauch
      localStorage.setItem('snapshot_referral_code', ref);
    }
  }, []);

  const validateForm = () => {
    const newErrors = {};

    // Name validation (only for register)
    if (isRegister && !formData?.name?.trim()) {
      newErrors.name = 'Name ist erforderlich';
    } else if (isRegister && formData?.name?.trim().length < 2) {
      newErrors.name = 'Name muss mindestens 2 Zeichen lang sein';
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData?.email?.trim()) {
      newErrors.email = 'E-Mail ist erforderlich';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Ungültige E-Mail-Adresse';
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Passwort ist erforderlich';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Passwort muss mindestens 8 Zeichen lang sein';
    }

    // Confirm password (only for register)
    if (isRegister && formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwörter stimmen nicht überein';
    }

    // Terms acceptance (only for register)
    if (isRegister && !formData.acceptTerms) {
      newErrors.acceptTerms = 'Sie müssen die AGB akzeptieren';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;
    setIsLoading(true);
    setErrors({});

    try {
      // NEU: Für Login verwenden wir login-initiate
      const endpoint = isRegister 
        ? '/api/auth/register' 
        : '/api/auth/login-initiate';
      
      const payload = isRegister
        ? {
            name: formData?.name?.trim(),
            email: formData?.email?.toLowerCase()?.trim() || '',
            password: formData.password,
            accept_terms: formData.acceptTerms,
            ...(referralCode && referralCode.trim() ? { referral_code: referralCode } : {})
          }
        : {
            email: formData?.email?.toLowerCase()?.trim() || '',
            password: formData.password,
            two_factor_code: twoFactorCode || undefined
          };

      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await res.json();

      if (!res.ok) {
        // 2FA erforderlich?
        if (res.status === 403 && data.detail === '2FA_REQUIRED') {
          setRequires2FA(true);
          setIsLoading(false);
          return;
        }

        // Login mit noch unbestätigter E-Mail → Hinweis + Resend-Button.
        if (res.status === 403 && data.detail === 'EMAIL_NOT_VERIFIED') {
          setLoginEmailUnverified(true);
          setVerificationSentEmail(formData?.email?.toLowerCase()?.trim() || '');
          setIsLoading(false);
          return;
        }

        let errorDetail = data.detail || 'Ein Fehler ist aufgetreten';
        
        if (Array.isArray(errorDetail) && errorDetail.length > 0) {
          const firstError = errorDetail[0];
          errorDetail = firstError.msg || firstError.type || 'Validierungsfehler';
        }
        
        const errorMap = {
          'String should have at least 2 characters': 'Bitte gib deinen vollständigen Namen (Vor- und Nachname) ein.',
          'The string did not match the expected pattern': 'Bitte gib deinen vollständigen Namen (Vor- und Nachname) ein.',
          'value is not a valid email address': 'Die E-Mail-Adresse ist ungültig.',
          'Ein Account mit dieser E-Mail-Adresse existiert bereits': 'Diese E-Mail-Adresse ist bereits registriert.',
          'E-Mail oder Passwort falsch': 'E-Mail oder Passwort ist falsch. Bitte überprüfe deine Eingaben.',
          'Dieser Account wurde mit Google OAuth erstellt': 'Dieser Account wurde mit Google erstellt. Bitte verwende Google Login.'
        };
        
        let friendlyError = errorMap[errorDetail] || errorDetail;
        
        if (errorDetail.includes('pattern') || errorDetail.includes('string did not match') || errorDetail.includes('at least 2 characters')) {
          friendlyError = 'Bitte gib deinen vollständigen Namen (Vor- und Nachname) ein.';
        } else if (errorDetail.includes('email') && errorDetail.includes('valid')) {
          friendlyError = 'Die E-Mail-Adresse ist ungültig.';
        }
        
        throw new Error(friendlyError);
      }

      // Double-Opt-In: Register gibt KEIN Token zurück, sondern nur
      // eine Aufforderung, die Bestätigungs-Mail zu öffnen.
      if (isRegister && data.status === 'verification_sent') {
        setVerificationSentEmail(data.email || formData?.email?.toLowerCase()?.trim() || '');
        setLoginEmailUnverified(false);
        setIsLoading(false);
        return;
      }

      // NEU: Prüfe ob E-Mail-Code erforderlich
      if (!isRegister && data.requires_email_code) {
        // E-Mail-Code Modal öffnen
        setPendingUserId(data.user_id);
        setShowEmailCodeModal(true);
        setIsLoading(false);
        return;
      }

      // Normaler Flow: Token direkt erhalten (Login ohne Step-Up)
      if (data.token) {
        await handleSuccessfulAuth(data);
      }

    } catch (error) {
      console.error('Auth error:', error);
      setErrors({ 
        general: error.message || 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  // NEU: E-Mail-Code verifizieren
  const handleVerifyEmailCode = async (code) => {
    setIsVerifyingCode(true);
    setEmailCodeError('');

    try {
      const res = await fetch(`${API_URL}/api/auth/login-verify-email-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData?.email?.toLowerCase()?.trim() || '',
          code
        })
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.detail || 'Code ungültig');
      }

      // JWT erhalten
      await handleSuccessfulAuth(data);
      setShowEmailCodeModal(false);

    } catch (error) {
      console.error('Code verification error:', error);
      setEmailCodeError(error.message);
    } finally {
      setIsVerifyingCode(false);
    }
  };

  // NEU: Erfolgreiche Auth behandeln
  const handleSuccessfulAuth = async (data) => {
    localStorage.setItem('snapshot_token', data.token);
    localStorage.setItem('snapshot_user', JSON.stringify(data.user));
    localStorage.setItem('snapshot_beta_access', 'granted');
    
    // DSGVO Consent-Logging bei Registrierung
    if (isRegister && data.user) {
      try {
        const ipHash = await crypto.subtle.digest('SHA-256', new TextEncoder().encode('client-ip-placeholder'))
          .then(buf => Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join(''));
        
        await apiClient.post('/api/consents', {
          user_id: data.user.id,
          email: data.user.email,
          avatar_id: null,
          policy_version: '2025-11-01',
          consent_type: 'registration',
          consents: {
            terms: true,
            privacy: true,
            imageProcessing: true,
            age: true
          },
          ip_hash: ipHash,
          timestamp: new Date().toISOString()
        });
        console.log('✅ Consent logged for registration');
      } catch (err) {
        console.warn('⚠️ Consent logging failed (non-critical):', err);
      }
    }
    
    // Clear cache
    localStorage.removeItem('generations_cache');
    localStorage.removeItem('last_known_avatars');
    localStorage.removeItem('gallery_cache');

    if (onSuccess) {
      onSuccess(data.user);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  // Double-Opt-In: Resend-Handler für den Bestätigungs-Link.
  const handleResendVerification = async () => {
    if (!verificationSentEmail || isResending) return;
    setIsResending(true);
    setResendMessage('');
    try {
      await fetch(`${API_URL}/api/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: verificationSentEmail })
      });
      setResendMessage('Wir haben dir den Link erneut zugeschickt.');
    } catch (err) {
      setResendMessage('Senden fehlgeschlagen. Bitte später erneut versuchen.');
    } finally {
      setIsResending(false);
    }
  };

  // Double-Opt-In: Bestätigungs-Screen nach Registrierung ODER Login ohne Verifikation.
  if (verificationSentEmail) {
    return (
      <div className="min-h-screen bg-black grain-texture flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#10B981]/20 border border-[#10B981]/40 flex items-center justify-center">
              <Mail className="w-8 h-8 text-[#10B981]" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-3">Prüfe deine E-Mails</h1>
            <p className="text-white/70 leading-relaxed">
              {loginEmailUnverified
                ? 'Dein Account ist noch nicht bestätigt.'
                : 'Vielen Dank für deine Registrierung.'}{' '}
              Wir haben dir einen Bestätigungs-Link an
            </p>
            <p className="text-[#10B981] font-bold mt-1 break-all">{verificationSentEmail}</p>
            <p className="text-white/70 mt-3">
              geschickt. Klicke den Link in der E-Mail, um deinen Account freizuschalten — du wirst anschließend automatisch eingeloggt.
            </p>
            <p className="text-white/40 text-sm mt-4">
              Der Link ist 24 Stunden gültig. Schau auch im Spam-Ordner nach.
            </p>
          </div>

          <button
            type="button"
            onClick={handleResendVerification}
            disabled={isResending}
            className="w-full py-3 rounded-xl bg-white/10 hover:bg-white/15 border border-white/20 text-white font-semibold transition-all disabled:opacity-50"
          >
            {isResending ? 'Wird gesendet …' : 'Bestätigungs-Link erneut senden'}
          </button>

          {resendMessage && (
            <p className="text-center text-sm text-white/60 mt-3">{resendMessage}</p>
          )}

          <button
            type="button"
            onClick={() => {
              setVerificationSentEmail(null);
              setLoginEmailUnverified(false);
              setResendMessage('');
              if (onBack) onBack();
            }}
            className="w-full mt-4 text-white/50 hover:text-white/80 text-sm underline"
          >
            Zurück
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black grain-texture flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">

        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">
            {isRegister ? 'Registrieren' : 'Anmelden'}
          </h1>
          <p className="text-white/60">
            {isRegister 
              ? 'Erstelle deinen SNAPSHOTPRO Account' 
              : 'Willkommen zurück bei SNAPSHOTPRO'}
          </p>
        </div>

        {/* Form */}
        <form ref={formRef} onSubmit={handleSubmit} className="space-y-5">
          
          {/* General Error */}
          {errors.general && (
            <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-start gap-3" data-testid="error-message">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-500">{errors.general}</p>
            </div>
          )}

          {/* Terms Checkbox (Register only) — ZUERST, gilt auch für Google */}
          {isRegister && (
            <div className="bg-white/5 border border-white/20 rounded-xl p-5">
              <div className="flex items-start gap-3">
                <div className="relative flex-shrink-0 mt-0.5">
                  <input
                    type="checkbox"
                    name="acceptTerms"
                    checked={formData.acceptTerms}
                    onChange={handleChange}
                    className="w-5 h-5 rounded border-2 border-white/30 bg-black text-[#10B981] focus:ring-2 focus:ring-[#10B981] cursor-pointer"
                    data-testid="terms-checkbox"
                  />
                </div>
                <label htmlFor="acceptTerms" className="text-sm text-white/80 leading-relaxed cursor-pointer">
                  <strong>Ich bestätige folgendes:</strong>
                  <ul className="mt-2 space-y-1 text-white/70 text-xs pl-4">
                    <li>• Ich besitze alle Rechte an hochgeladenen Bildern</li>
                    <li>• Ich bin mind. 18 Jahre alt oder habe elterliche Einwilligung</li>
                    <li>• Ich akzeptiere die Verarbeitung meiner Daten gemäß DSGVO</li>
                  </ul>
                  <p className="mt-3 text-white/70">
                    Ich stimme den{' '}
                    <a href="/agb" target="_blank" rel="noopener noreferrer" className="text-[#10B981] underline hover:text-[#059669] font-semibold" onClick={(e) => e.stopPropagation()}>
                      AGB
                    </a>
                    {' '}und{' '}
                    <a href="/datenschutz" target="_blank" rel="noopener noreferrer" className="text-[#10B981] underline hover:text-[#059669] font-semibold" onClick={(e) => e.stopPropagation()}>
                      Datenschutzbestimmungen
                    </a>
                    {' '}zu — auch bei Anmeldung mit Google.
                  </p>
                </label>
              </div>
              {errors.acceptTerms && (
                <p className="mt-2 text-xs text-red-500 pl-8">{errors.acceptTerms}</p>
              )}
            </div>
          )}

          {/* Google Button — gestylter Custom-Button, Google's Button liegt transparent darüber */}
          <div className={isRegister && !formData.acceptTerms ? 'opacity-40 pointer-events-none select-none' : ''}>
            <div className="relative w-full">
              {/* Sichtbarer Custom-Button */}
              <button
                type="button"
                disabled={isLoading}
                className="w-full py-3 px-6 bg-black/50 border-2 border-[#10B981]/30 text-white rounded-xl font-semibold text-base hover:border-[#10B981]/60 hover:bg-black/70 transition-all disabled:opacity-50 flex items-center justify-center gap-3 backdrop-blur-sm"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#10B981" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                  <path fill="#10B981" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#059669" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#059669" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                {isRegister ? 'Mit Google registrieren' : 'Mit Google anmelden'}
              </button>
              {/* Google's offizieller Button: über dem Custom-Button, transparent — Klick wird abgefangen */}
              <div
                ref={googleBtnRef}
                className="absolute inset-0 opacity-0 [&>*]:!w-full [&>*]:!h-full"
                style={{ pointerEvents: 'auto' }}
              />
            </div>
            {isRegister && !formData.acceptTerms && (
              <p className="mt-2 text-center text-xs text-white/50">
                Bitte zuerst Bedingungen oben akzeptieren
              </p>
            )}
          </div>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10"></div>
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-black px-3 text-white/40">oder mit E-Mail</span>
            </div>
          </div>

          {/* Name (Register only) */}
          {isRegister && (
            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">
                Vollständiger Name
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Max Mustermann"
                  className={`w-full pl-11 pr-4 py-3 bg-black border-2 rounded-xl text-white placeholder:text-white/40 focus:outline-none transition-all ${
                    errors.name 
                      ? 'border-red-500/50 focus:border-red-500' 
                      : 'border-white/30 focus:border-[#10B981]'
                  }`}
                  data-testid="name-input"
                />
              </div>
              {errors.name && (
                <p className="mt-1 text-xs text-red-500">{errors.name}</p>
              )}
            </div>
          )}

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">
              E-Mail-Adresse
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="mail@beispiel.de"
                className={`w-full pl-11 pr-4 py-3 bg-black border-2 rounded-xl text-white placeholder:text-white/40 focus:outline-none transition-all ${
                  errors.email 
                    ? 'border-red-500/50 focus:border-red-500' 
                    : 'border-white/30 focus:border-[#10B981]'
                }`}
                data-testid="email-input"
              />
            </div>
            {errors.email && (
              <p className="mt-1 text-xs text-red-500">{errors.email}</p>
            )}
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">
              Passwort
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Mindestens 8 Zeichen"
                className={`w-full pl-11 pr-12 py-3 bg-black border-2 rounded-xl text-white placeholder:text-white/40 focus:outline-none transition-all ${
                  errors.password 
                    ? 'border-red-500/50 focus:border-red-500' 
                    : 'border-white/30 focus:border-[#10B981]'
                }`}
                data-testid="password-input"
              />
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowPassword(!showPassword); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-white/40 hover:text-white/60 transition-colors z-10 hover:!-translate-y-1/2"
              >
                {showPassword ? (
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
            {errors.password && (
              <p className="mt-1 text-xs text-red-500">{errors.password}</p>
            )}
          </div>


          {/* 2FA-Code (nur wenn erforderlich) */}
          {requires2FA && !isRegister && (
            <div>
              <label className="block text-white/80 text-sm font-medium mb-2">
                2FA-Code
              </label>
              <div className="relative">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#10B981]/50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <input
                  type="text"
                  value={twoFactorCode}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setTwoFactorCode(val);
                    if (val.length === 6) {
                      setTimeout(() => formRef.current?.requestSubmit(), 50);
                    }
                  }}
                  placeholder="123456"
                  maxLength={6}
                  className="w-full pl-11 pr-4 py-3 bg-black border-2 border-[#10B981]/50 rounded-xl text-white text-center text-2xl tracking-widest placeholder:text-white/40 focus:outline-none focus:border-[#10B981] transition-all"
                  data-testid="2fa-code-input"
                  autoFocus
                />
              </div>
              <p className="mt-1 text-xs text-[#10B981]/80">Gib deinen 6-stelligen Code aus der Authenticator-App ein</p>
            </div>
          )}


          {/* Confirm Password (Register only) */}
          {isRegister && (
            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">
                Passwort bestätigen
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Passwort wiederholen"
                  className={`w-full pl-11 pr-4 py-3 bg-black border-2 rounded-xl text-white placeholder:text-white/40 focus:outline-none transition-all ${
                    errors.confirmPassword 
                      ? 'border-red-500/50 focus:border-red-500' 
                      : 'border-white/30 focus:border-[#10B981]'
                  }`}
                  data-testid="confirm-password-input"
                />
              </div>
              {errors.confirmPassword && (
                <p className="mt-1 text-xs text-red-500">{errors.confirmPassword}</p>
              )}
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-4 px-6 bg-[#10B981] text-black rounded-xl font-bold text-lg hover:bg-[#059669] transition-all disabled:opacity-50 disabled:cursor-not-allowed glow-green pulse-glow flex items-center justify-center gap-2"
            data-testid="submit-button"
          >
            {isLoading ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                {isRegister ? 'Registriere...' : 'Melde an...'}
              </>
            ) : (
              <>
                {isRegister ? 'Account erstellen' : 'Anmelden'}
              </>
            )}
          </button>
        </form>

        {/* Switch Mode */}
        <div className="mt-6 text-center">
          <p className="text-white/60 text-sm">
            {isRegister ? 'Bereits ein Account?' : 'Noch kein Account?'}
            {' '}
            <button
              onClick={onBack}
              className="text-[#10B981] hover:underline font-semibold"
              data-testid="switch-mode-button"
            >
              {isRegister ? 'Jetzt anmelden' : 'Jetzt registrieren'}
            </button>
          </p>
          
          {/* Passwort vergessen - nur bei Login */}
          {!isRegister && (
            <button
              onClick={() => setShowForgotPassword(true)}
              className="mt-3 text-white/50 hover:text-[#10B981] text-sm transition-colors"
              data-testid="forgot-password-link"
            >
              Passwort vergessen?
            </button>
          )}
        </div>

        {/* Back to Landing */}
        <div className="mt-4 text-center">
          <button
            onClick={onBack}
            className="text-white/50 hover:text-white/70 text-sm transition-colors"
            data-testid="back-button"
          >
            ← Zurück zur Startseite
          </button>
        </div>

      </div>
      
      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <ForgotPasswordModal onClose={() => setShowForgotPassword(false)} />
      )}
      
      {/* Email Code Modal */}
      {showEmailCodeModal && (
        <EmailCodeModal
          isOpen={showEmailCodeModal}
          onClose={() => {
            setShowEmailCodeModal(false);
            setPendingUserId(null);
            setEmailCodeError('');
          }}
          onVerify={handleVerifyEmailCode}
          email={formData.email}
          isLoading={isVerifyingCode}
          error={emailCodeError}
        />
      )}
    </div>
  );
}
